import { callEdge } from "@/lib/payments/api.ts";
import { readApplePayRuntimeCapability } from "@/lib/applePayCapability.js";
import { readDigitalWalletRuntimeCapability } from "@/lib/digitalWalletCapability.js";

const KNOWN_METHODS = new Set(["card", "instant_eft", "capitec_pay", "qr", "apple_pay", "google_pay", "samsung_pay"]);
export function normalizeAppointmentPaystackMethods(value) {
  const values = Array.isArray(value) ? value : [];
  const result = [];
  for (const candidate of values) {
    const method = String(candidate || "").trim().toLowerCase();
    if (KNOWN_METHODS.has(method) && !result.includes(method)) result.push(method);
  }
  return result;
}

export function isBrowserEligibleAppointmentPaystackMethod(methodId) {
  const method = String(methodId || "").trim().toLowerCase();
  if (method === "apple_pay") return readApplePayRuntimeCapability().canMakePayments;
  return readDigitalWalletRuntimeCapability(method).eligible;
}

export function filterAppointmentPaystackMethodsForPatient(methods) {
  return (Array.isArray(methods) ? methods : []).filter((method) =>
    isBrowserEligibleAppointmentPaystackMethod(method?.id || method),
  );
}

function publishAppointmentPaymentCapabilitySnapshot(patch) {
  if (typeof window === "undefined") return;
  window.__afrimedAppointmentPaymentCapability = {
    ...(window.__afrimedAppointmentPaymentCapability || {}),
    ...patch,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Runtime-only diagnostics for support of the Patient booking method list.
 * It contains no patient, appointment, payment, or credential data.
 */
export function recordAppointmentPaymentCapabilityDiagnostics({
  providerMethods = [],
  clinicianMeta = {},
  clinicianEnabledMethods = [],
  patientMethods = [],
  capabilitySource = "unknown",
} = {}) {
  const applePay = readApplePayRuntimeCapability();
  const normalizedProviderMethods = normalizeAppointmentPaystackMethods(providerMethods);
  if ((Array.isArray(providerMethods) ? providerMethods : []).includes("mtn_momo")) {
    normalizedProviderMethods.push("mtn_momo");
  }
  const clinicianMethodIds = (Array.isArray(clinicianEnabledMethods) ? clinicianEnabledMethods : [])
    .map((method) => String(method?.id || method || "").trim().toLowerCase())
    .filter(Boolean);
  const patientMethodIds = (Array.isArray(patientMethods) ? patientMethods : [])
    .map((method) => String(method?.id || method || "").trim().toLowerCase())
    .filter(Boolean);
  const providerSupportsApplePay = normalizedProviderMethods.includes("apple_pay");
  const clinicianAcceptsApplePay = clinicianMeta?.paystack_apple_pay === true;
  const clinicianMethodIncludesApplePay = clinicianMethodIds.includes("apple_pay");
  const applePayRemovalReason = !providerSupportsApplePay
    ? "provider_capability_missing"
    : !clinicianAcceptsApplePay
      ? "clinician_preference_disabled"
      : !clinicianMethodIncludesApplePay
        ? "clinician_method_not_derived"
        : !applePay.canMakePayments
          ? applePay.reason
          : patientMethodIds.includes("apple_pay")
            ? "rendered"
            : "patient_filter_unexpectedly_removed";

  const snapshot = {
    capabilitySource,
    providerMethods: normalizedProviderMethods,
    clinicianApplePayEnabled: clinicianAcceptsApplePay,
    clinicianEnabledMethods: clinicianMethodIds,
    applePay,
    patientMethods: patientMethodIds,
    applePayRemovalReason,
  };
  publishAppointmentPaymentCapabilitySnapshot(snapshot);
  return snapshot;
}

// This is intentionally a server-issued capability snapshot. Paystack does
// not publish a merchant-channel capability endpoint; deployment configuration
// is the explicit, auditable statement of the channels confirmed for that
// merchant environment. The browser can only narrow it further for device
// eligibility (currently Apple Pay).
export async function loadAppointmentPaystackCapabilities({ forClinicianSetup = false, includeEnvironment = false } = {}) {
  try {
    const result = await callEdge("paystack-appointment-checkout", { action: "capabilities" });
    if (!Array.isArray(result?.methods)) throw new Error("payment_capabilities_invalid");
    const methods = normalizeAppointmentPaystackMethods(result.methods);
    publishAppointmentPaymentCapabilitySnapshot({
      providerCapabilityRequest: { source: "edge", methods },
    });
    const filtered = forClinicianSetup ? methods : filterAppointmentPaystackMethodsForPatient(methods);
    return includeEnvironment ? { methods: filtered, cardFirst: result.launch_scope === 'card_first' } : filtered;
  } catch {
    throw new Error("payment_capabilities_unavailable");
  }
}
